import { useEffect, useState } from 'react';
import { AdminSidebar } from '../../components/admin/AdminSidebar';
import { getAdminDashboardStats, type DashboardStats } from '@shared/services/admin-api';
import { Users, Crown, Gift, UserPlus } from 'lucide-react';

export function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAdminDashboardStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to load stats:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-dark-bg">
      <div className="flex">
        <AdminSidebar />
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <h1 className="font-heading text-2xl font-bold mb-1">Dashboard</h1>
          <p className="text-content-muted text-sm mb-6">CogniPrint administration overview</p>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : stats ? (
            <>
              {/* Stat cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard
                  icon={<Users size={20} />}
                  label="Total Users"
                  value={stats.totalUsers}
                  color="#6C5CE7"
                />
                <StatCard
                  icon={<Crown size={20} />}
                  label="Paid Users"
                  value={stats.tierBreakdown.learner + stats.tierBreakdown.pro + stats.tierBreakdown.teams}
                  color="#F0A500"
                />
                <StatCard
                  icon={<Gift size={20} />}
                  label="Active Grants"
                  value={stats.activeGrants}
                  color="#0ABFBC"
                />
                <StatCard
                  icon={<UserPlus size={20} />}
                  label="Signups (7d)"
                  value={stats.recentSignups.length}
                  color="#A39BE0"
                />
              </div>

              {/* Tier breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-2xl bg-dark-surface border border-dark-border p-5">
                  <h2 className="font-heading text-base font-bold mb-4">Subscription Breakdown</h2>
                  <div className="space-y-3">
                    <TierRow label="Free" count={stats.tierBreakdown.free} total={stats.totalUsers} color="#6B7280" />
                    <TierRow label="Learner" count={stats.tierBreakdown.learner} total={stats.totalUsers} color="#6C5CE7" />
                    <TierRow label="Pro" count={stats.tierBreakdown.pro} total={stats.totalUsers} color="#F0A500" />
                    <TierRow label="Teams" count={stats.tierBreakdown.teams} total={stats.totalUsers} color="#0ABFBC" />
                  </div>
                </div>

                <div className="rounded-2xl bg-dark-surface border border-dark-border p-5">
                  <h2 className="font-heading text-base font-bold mb-4">Recent Signups</h2>
                  {stats.recentSignups.length === 0 ? (
                    <p className="text-sm text-content-muted">No signups in the last 7 days.</p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {stats.recentSignups.map((u) => (
                        <div
                          key={u.uid}
                          className="flex items-center justify-between rounded-lg p-3 bg-dark-subtle border border-dark-border"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-content truncate">
                              {u.displayName ?? u.email ?? u.uid}
                            </div>
                            <div className="text-[11px] text-content-muted">
                              {u.email}
                            </div>
                          </div>
                          <div className="text-[11px] font-code text-content-muted shrink-0 ml-3">
                            {new Date(u.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <p className="text-content-muted text-sm">Failed to load dashboard stats.</p>
          )}
        </main>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="rounded-2xl bg-dark-surface border border-dark-border p-5">
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: `${color}18`, color }}
        >
          {icon}
        </div>
      </div>
      <div className="font-heading text-2xl font-bold text-white mb-0.5">{value}</div>
      <div className="text-[12px] text-content-muted">{label}</div>
    </div>
  );
}

function TierRow({ label, count, total, color }: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-content">{label}</span>
        <span className="text-sm font-code text-content-muted">
          {count} <span className="text-[10px]">({pct.toFixed(0)}%)</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-dark-subtle overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}
